// useGramUsdRate — the live GRAM (= TON) / USD rate.
//
// The backend bakes the event-driven TON/USD rate into each pool's price_usd
// (ADR-026), so for a TON-quoted pool `price_usd / last_price` IS the GRAM/USD
// rate. We fetch a small top-volume slice once when `enabled` flips true and
// take the first pool carrying both fields whose primary pool is quoted in
// TON: a token whose primary pool is USD₮-quoted has last_price in USD₮, and
// its ratio (≈1) would poison the cached rate for every later visit. Returns null until loaded (callers fall
// back to a sample rate meanwhile). Read-only — does not touch the data layer.

import { useEffect, useState } from 'react'
import { fetchTokens } from '../api/tokens'
import type { TokenSummary } from '../api/types'
import { isTonQuoteToken } from '../lib/tonQuote'

const STORAGE_KEY = 'wils.gramUsdRate'
let memoryRate: number | null = null

function readCachedRate(): number | null {
  if (memoryRate != null) return memoryRate
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    const value = raw ? Number(raw) : Number.NaN
    if (Number.isFinite(value) && value > 0) {
      memoryRate = value
      return value
    }
  } catch {
    // localStorage blocked
  }
  return null
}

function writeCachedRate(value: number): void {
  memoryRate = value
  try {
    localStorage.setItem(STORAGE_KEY, String(value))
  } catch {
    // localStorage blocked
  }
}

export function useGramUsdRate(enabled: boolean): number | null {
  const [rate, setRate] = useState<number | null>(readCachedRate)

  useEffect(() => {
    const cached = readCachedRate()
    if (cached != null) {
      setRate(cached)
      return
    }
    if (!enabled) return
    let alive = true
    fetchTokens({ limit: 20, sort: 'volume_24h', window: '24h' })
      .then((r) => {
        if (!alive) return
        const t = r.items.find(isGramRateSource)
        if (t?.price_usd != null && t.last_price > 0) {
          const v = t.price_usd / t.last_price
          if (Number.isFinite(v) && v > 0) {
            writeCachedRate(v)
            setRate(v)
          }
        }
      })
      .catch(() => {})
    return () => {
      alive = false
    }
  }, [enabled])

  return rate
}

// isGramRateSource: a row whose price_usd / last_price is TON/USD. A missing
// primary_quote_token predates pools without TON (every primary pool was TON).
export function isGramRateSource(x: TokenSummary): boolean {
  if (x.price_usd == null || !(x.last_price > 0)) return false
  return x.primary_quote_token === undefined || isTonQuoteToken(x.primary_quote_token)
}
